/* eslint complexity: ["error", 10] */
import { HourGlassIcon, Heading as BaseHeading, Paragraph } from '@cof/plastic-components';
import { css, styled } from 'styled-components';
import { useState, useEffect, useContext } from 'react';

import Header from '../../components/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import getResultPage from '../../components/Quotation/Result/getResult';
import Main from '../../components/Main';
import useBackground from '../../utilities/useBackground';
import { decisions } from '../../utilities/constants';
import { useBrand } from '../../BrandContext';
import { agreementUrlBuilder } from '../../components/Quotation/Result/agreementUrlBuilder';
import useTechnicalError from '../../utilities/useTechnicalError/useTechnicalError';
import ErrorContext from '../../utilities/errorContext';
import { useObCompleteData } from '../../data/data';
import { addNewRelicPageAction } from '../../utilities/newRelic';
import { useAggregatorConsumerName } from '../../utilities/aggregatorConsumerNameProvider';
import { isAggregatorConsumer, isApiPartner } from '../../utilities/utils';
import { verifyUrlBuilder } from '../../utilities/verifyUrlBuilder';

const Heading = styled(BaseHeading).attrs(() => ({ level: '1', size: 'small', mt: 'md' }))`
    ${({ theme }: any) => css`
        ${theme.components.OBPendingSpinnerHeading};
        font-weight: 600;
    `}
`;

const handleResponse = ({
    data,
    brand,
    quotation,
    consumerName,
    productCode,
    pcr,
    s,
    sessionTerminationToken,
    setResultData
}: any) => {
    if (data == 'Session expired') {
        window.location.assign(
            verifyUrlBuilder({ brand, quotation, consumerName, productCode, pcr, s, sessionTerminationToken })
        );
    } else if (data?.reason !== decisions.MATCH_AND_OFFER) {
        setResultData(data?.reason);
    } else {
        window.location.assign(agreementUrlBuilder({ ...data, brand, aggregatorConsumerName: consumerName }).href);
    }
};

const getConditionalParams = ({ consumerName, brand, urlParams }: any) => ({
    pcr: isAggregatorConsumer(consumerName) || isApiPartner(brand) ? urlParams.get('pcr') : undefined,
    s: isApiPartner(brand) ? urlParams.get('s') : undefined
});

const OpenBankingPending = () => {
    const [useSecondSpinnerTitle, setUseSecondSpinnerTitle] = useState(false);
    const [resultData, setResultData] = useState(null);
    const urlParams = new URLSearchParams(window.location.search);
    const brand = useBrand();
    const quotation = urlParams.get('quotation');
    const sessionTerminationToken = urlParams.get('sessionTerminationToken');
    const correlationId = urlParams.get('correlationId');
    const productCode = urlParams.get('p');
    const consumerName = urlParams.get('consumerName');
    const { pcr, s } = getConditionalParams({ consumerName, brand, urlParams });
    const background = useBackground('openBanking');
    // @ts-expect-error TS2339: Property 'setError' does not exist on type ''.
    const { setError } = useContext(ErrorContext);
    // @ts-expect-error TS2488: Type '' must have a '[Symbol.iterator]()' method that returns an iterator.
    const [, setAggregatorConsumerName] = useAggregatorConsumerName();

    // eslint-disable-next-line no-unused-vars
    // @ts-expect-error TS2339: Property 'response' does not exist on type 'boolean | ((reqData: any) => void) |  error: string; res
    const [executeObComplete, loading, { response, error, data }] = useObCompleteData({
        quotation,
        sessionTerminationToken,
        ...(productCode && { productCode }),
        ...(correlationId && { correlationId }),
        ...(pcr && { pcr }),
        ...(s && { s })
    });

    useTechnicalError(error);

    useEffect(() => {
        if (consumerName && isAggregatorConsumer(consumerName)) {
            setAggregatorConsumerName(consumerName);
        }
    }, [consumerName, setAggregatorConsumerName]);

    useEffect(() => {
        if (!quotation) {
            setError();
        } else {
            // @ts-expect-error TS2349: This expression is not callable.
            executeObComplete();
            addNewRelicPageAction('OB Pending No Quote Token', { brand });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    setTimeout(() => {
        setUseSecondSpinnerTitle(true);
    }, 10000);

    useEffect(() => {
        if (quotation && response) {
            handleResponse({
                data,
                brand,
                quotation,
                consumerName,
                productCode,
                pcr,
                s,
                sessionTerminationToken,
                setResultData
            });
        }
    }, [response]);

    return (
        <>
            {resultData ? (
                // @ts-expect-error TS2554: Expected 4 arguments, but got 2.
                getResultPage(resultData, data.userData)
            ) : (
                <>
                    <Header isObPending={true} />
                    <Main bg={background} data-qa-id="ob-pending-page">
                        <LoadingSpinner
                            Icon={HourGlassIcon}
                            features={{ resultSpinner: true }}
                            data-qa-id="ob-pending-spinner"
                        >
                            {/* @ts-expect-error TS2769: No overload matches this call. */}
                            <Heading data-qa-id="OB-spinner-heading" $brand={brand} paddingX="xl">
                                {useSecondSpinnerTitle ? 'Final checks, nearly done!' : 'Fetching your details'}
                            </Heading>
                            <Paragraph variant={brand === 'ocean' ? 'dark' : 'light'} data-qa-id="OB-spinner-paragraph">
                                This could take up to a minute
                            </Paragraph>
                        </LoadingSpinner>
                    </Main>
                </>
            )}
        </>
    );
};

export default OpenBankingPending;
